import React, { Suspense, lazy } from 'react';
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './queryClient';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BrandingProvider, useBranding } from './contexts/BrandingContext';
import { PreferencesProvider } from './contexts/PreferencesContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { FiltersProvider } from './contexts/FiltersContext';
import { UIProvider, useUI } from './contexts/UIContext';
import ErrorBoundary from './components/ErrorBoundary';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import CallDetailsModal from './components/CallDetailsModal';
import { AssistantProvider, useAssistant } from './components/Assistant/AssistantContext';
import NotFound from './components/NotFound';
import { Spinner } from './components/ui/Skeleton';
import './index.css';

/* ── Routes ───────────────────────────────────────────────────────────────── */
const AssistantPanel = lazy(() => import('./components/Assistant/AssistantPanel'));
const WhatsNew = lazy(() => import('./components/WhatsNew'));
const Overview = lazy(() => import('./components/Overview'));
const Outlook = lazy(() => import('./components/Outlook'));
const CallRecords = lazy(() => import('./components/CallRecords'));
const Intelligence = lazy(() => import('./components/InsightsCommand'));
const FollowUps = lazy(() => import('./components/ActionItems'));
const Performance = lazy(() => import('./components/Agents'));
const Settings = lazy(() => import('./components/Settings'));
const AIInsights = lazy(() => import('./components/Settings/AIInsights'));
const Integrations = lazy(() => import('./components/Settings/Integrations'));
const ManualUploadPage = lazy(() => import('./components/Settings/ManualUploadPage'));
const Notifications = lazy(() => import('./components/Settings/Notifications'));
const CustomerManagement = lazy(() => import('./components/Settings/CustomerManagement'));
const LeadStatuses = lazy(() => import('./components/Settings/LeadStatuses'));
const LeadCustomFields = lazy(() => import('./components/Settings/LeadCustomFields'));
const MarketingSources = lazy(() => import('./components/Settings/MarketingSources'));
const Usage = lazy(() => import('./components/Settings/Usage'));
const Preferences = lazy(() => import('./components/Settings/Preferences'));
const UserManagement = lazy(() => import('./components/UserManagement/UserManagement'));
const WhatsApp = lazy(() => import('./components/WhatsApp'));
const Leads = lazy(() => import('./components/Leads'));

/* ── Unauthenticated ──────────────────────────────────────────────────────── */
const LandingPage = lazy(() => import('./components/LandingPage'));
const Auth = lazy(() => import('./components/Auth'));
const TenantSignup = lazy(() => import('./components/Signup'));
const InviteUserSignup = lazy(() => import('./components/InviteUserSignup'));
const ThankYou = lazy(() => import('./components/ThankYou'));
const ResetPasswordRequest = lazy(() => import('./components/ResetPasswordRequest'));
const UpdatePassword = lazy(() => import('./components/UpdatePassword'));
const TrialExpired = lazy(() => import('./components/TrialExpired'));
const AccountPaused = lazy(() => import('./components/AccountPaused'));
const PendingReview = lazy(() => import('./components/Onboarding/PendingReview'));
const VerifyEmail = lazy(() => import('./components/Onboarding/VerifyEmail'));
const AdminLogin = lazy(() => import('./components/Admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./components/Admin/AdminDashboard'));
const TenantDetailPage = lazy(() => import('./components/Admin/TenantDetailPage'));

const RouteSpinner = () => (
  <div className="flex min-h-[50vh] items-center justify-center">
    <span className="flex items-center gap-2 text-body text-ink-500">
      <Spinner size={16} />
      Loading…
    </span>
  </div>
);

/** Preserve the query string across a rename, e.g. old ?phone= deep links. */
const RedirectTo = ({ to }) => {
  const [params] = useSearchParams();
  const qs = params.toString();
  return <Navigate to={qs ? `${to}?${qs}` : to} replace />;
};

/**
 * A logged-out deep link used to render a blank page. It now goes to the login
 * screen carrying where the user was trying to get to.
 */
const RequireAuthRedirect = () => {
  const location = useLocation();
  const next = `${location.pathname}${location.search}`;
  const suffix = next && next !== '/' ? `?next=${encodeURIComponent(next)}` : '';
  return <Navigate to={`/login${suffix}`} replace />;
};

/**
 * Owner-only routes were gated in the SIDEBAR only — the routes themselves stayed
 * open, so an agent who reached one got a silently empty screen instead of an
 * explanation. This is the guard the nav was pretending to be.
 */
const OwnerRoute = ({ children }) => {
  const { isOwner } = useAuth();
  if (isOwner) return children;
  return (
    <div className="mx-auto max-w-lg py-16 text-center">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-surface-2">
        <i className="fas fa-lock text-title text-ink-500" aria-hidden="true" />
      </div>
      <h1 className="text-heading-sm text-ink-900">Only owners can open this page</h1>
      <p className="mt-2 text-body text-ink-700">
        Ask an owner of this workspace to change it for you, or to change your role.
      </p>
    </div>
  );
};

function AppContent() {
  const { authenticated, authLoading, handleLogin, handleLogout } = useAuth();
  const { brandId, brandLoading } = useBranding();
  const { isSidebarOpen, isModalOpen, selectedCall, queueNav, toggleSidebar, handleCloseModal } = useUI();
  const { open: assistantOpen } = useAssistant();

  const handleRefreshData = () => queryClient.invalidateQueries();

  if (authLoading || brandLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas">
        <span className="flex items-center gap-2 text-body text-ink-500">
          <Spinner size={16} />
          Loading…
        </span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas text-ink-900">
      <Routes>
        {/* Public and account-state routes, available in both auth states. */}
        <Route path="/thank-you" element={<Suspense fallback={<RouteSpinner />}><ThankYou /></Suspense>} />
        <Route path="/reset-password" element={<Suspense fallback={<RouteSpinner />}><ResetPasswordRequest /></Suspense>} />
        <Route path="/update-password" element={<Suspense fallback={<RouteSpinner />}><UpdatePassword /></Suspense>} />
        <Route path="/trial-expired" element={<Suspense fallback={<RouteSpinner />}><TrialExpired /></Suspense>} />
        <Route path="/account-paused" element={<Suspense fallback={<RouteSpinner />}><AccountPaused /></Suspense>} />
        <Route path="/pending-review" element={<Suspense fallback={<RouteSpinner />}><PendingReview /></Suspense>} />
        <Route path="/verify-email" element={<Suspense fallback={<RouteSpinner />}><VerifyEmail /></Suspense>} />
        <Route path="/admin" element={<Suspense fallback={<RouteSpinner />}><AdminLogin onLogin={() => {}} /></Suspense>} />
        <Route path="/admin/dashboard" element={<Suspense fallback={<RouteSpinner />}><AdminDashboard /></Suspense>} />
        <Route path="/admin/tenants/:tenantId" element={<Suspense fallback={<RouteSpinner />}><TenantDetailPage /></Suspense>} />

        {!authenticated && (
          <>
            <Route
              path="/"
              element={
                brandId ? (
                  <Navigate to="/login" replace />
                ) : (
                  <Suspense fallback={<RouteSpinner />}><LandingPage /></Suspense>
                )
              }
            />
            <Route path="/login" element={<Suspense fallback={<RouteSpinner />}><Auth onLogin={handleLogin} /></Suspense>} />
            <Route path="/signup" element={<Suspense fallback={<RouteSpinner />}><TenantSignup onSignupSuccess={() => {}} onBackToSignin={() => {}} /></Suspense>} />
            <Route
              path="/join"
              element={
                <Suspense fallback={<RouteSpinner />}>
                  <InviteUserSignup onSignupSuccess={() => { window.location.href = '/login'; }} />
                </Suspense>
              }
            />
            {/* Anything else while logged out remembers where you were going. */}
            <Route path="*" element={<RequireAuthRedirect />} />
          </>
        )}

        {authenticated && (
          <Route
            path="/*"
            element={
              <>
                <a href="#main-content" className="skip-link">Skip to main content</a>
                <Sidebar isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
                {/* With the assistant open on a wide screen the page gives it room
                    rather than sitting underneath it. */}
                <div className={`min-h-screen lg:pl-sidebar ${assistantOpen ? 'xl:pr-[26rem]' : ''}`}>
                  <Header
                    toggleSidebar={toggleSidebar}
                    onRefreshData={handleRefreshData}
                    onLogout={handleLogout}
                  />
                  <main id="main-content" className="px-4 pb-16 pt-4 md:px-6 md:pt-6">
                    <ErrorBoundary>
                      <Suspense fallback={<RouteSpinner />}>
                        <Routes>
                          <Route path="/" element={<Overview />} />

                          <Route path="/outlook" element={<Outlook />} />
                          {/* main shipped this screen at /executive-summary; the
                              redirect keeps those links (and their ?date=) alive,
                              the way every other renamed route here does. */}
                          <Route path="/executive-summary" element={<RedirectTo to="/outlook" />} />

                          <Route path="/calls" element={<CallRecords />} />
                          <Route path="/call-records" element={<RedirectTo to="/calls" />} />

                          <Route path="/intelligence" element={<Intelligence />} />
                          <Route path="/insights-summary" element={<RedirectTo to="/intelligence" />} />

                          <Route path="/follow-ups" element={<FollowUps />} />
                          <Route path="/action-items" element={<RedirectTo to="/follow-ups" />} />

                          <Route path="/contacts" element={<Leads />} />
                          <Route path="/customers" element={<RedirectTo to="/contacts" />} />
                          <Route path="/leads" element={<RedirectTo to="/contacts" />} />

                          <Route path="/whatsapp" element={<WhatsApp />} />

                          <Route path="/performance" element={<Performance />} />
                          <Route path="/agents" element={<RedirectTo to="/performance" />} />

                          <Route path="/team" element={<OwnerRoute><UserManagement /></OwnerRoute>} />
                          <Route path="/users" element={<RedirectTo to="/team" />} />

                          <Route path="/settings" element={<OwnerRoute><Settings /></OwnerRoute>} />
                          <Route path="/settings/preferences" element={<Preferences />} />
                          <Route path="/settings/integrations" element={<OwnerRoute><Integrations /></OwnerRoute>} />
                          <Route path="/settings/integrations/upload" element={<OwnerRoute><ManualUploadPage /></OwnerRoute>} />
                          <Route path="/settings/ai-insights" element={<OwnerRoute><AIInsights /></OwnerRoute>} />
                          <Route path="/settings/notifications" element={<OwnerRoute><Notifications /></OwnerRoute>} />
                          <Route path="/settings/customer-management" element={<OwnerRoute><CustomerManagement /></OwnerRoute>} />
                          <Route path="/settings/lead-statuses" element={<OwnerRoute><LeadStatuses /></OwnerRoute>} />
                          <Route path="/settings/lead-custom-fields" element={<OwnerRoute><LeadCustomFields /></OwnerRoute>} />
                          <Route path="/settings/marketing-sources" element={<OwnerRoute><MarketingSources /></OwnerRoute>} />
                          <Route path="/settings/usage" element={<OwnerRoute><Usage /></OwnerRoute>} />
                          {/* Superseded by /settings/ai-insights, which edits the
                              same category options and notification criteria. */}
                          <Route path="/settings/categories" element={<Navigate to="/settings/ai-insights" replace />} />

                          <Route path="/login" element={<Navigate to="/" replace />} />
                          <Route path="/signup" element={<Navigate to="/" replace />} />
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                      </Suspense>
                    </ErrorBoundary>
                  </main>
                </div>
                <Suspense fallback={null}>
                  <AssistantPanel />
                  {/* Once per release, after login; see WhatsNew for the key. */}
                  <WhatsNew />
                </Suspense>
                <CallDetailsModal
                  isOpen={isModalOpen}
                  callData={selectedCall}
                  onClose={handleCloseModal}
                  {...(queueNav || {})}
                />
              </>
            }
          />
        )}
      </Routes>
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      {/* Outermost: it only sets a class on <html>, so nothing depends on it and
          every screen can read it. */}
      <ThemeProvider>
        <Router>
        <BrandingProvider>
          <AuthProvider>
            {/* Preferences before Filters: the default period is resolved in the
                tenant's timezone, so the filter defaults depend on it. */}
            <PreferencesProvider>
              <FiltersProvider>
                <UIProvider>
                  <AssistantProvider>
                    <AppContent />
                  </AssistantProvider>
                </UIProvider>
              </FiltersProvider>
            </PreferencesProvider>
          </AuthProvider>
        </BrandingProvider>
        </Router>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
